import { useMutation } from "@tanstack/react-query";
import { t } from "@lingui/core/macro";
import { i18n } from "@app/i18n";
import { authServerErrorDescriptor } from "shared/dto/auth/messages";
import { authClient } from "@/lib/AuthClient";
import { apiClient } from "@/lib/ApiClient";

/** 세션 유저의 타임존을 갱신한다. better-auth 클라이언트가 갱신 후 세션을
 * 자동으로 다시 읽어오므로 별도 캐시 무효화는 필요 없다. */
export function useUpdateTimezoneMutation() {
  return useMutation({
    mutationFn: async (timezone: string) => {
      const { error } = await authClient.updateUser({ timezone });
      if (error) {
        // better-auth 의 영어 message 대신 code 로 번역, 모르는 코드는 일반 폴백.
        const descriptor = authServerErrorDescriptor(error.code);
        throw new Error(descriptor ? i18n._(descriptor) : t(i18n)`타임존을 저장하지 못했습니다.`);
      }
    },
  });
}

/** 세션 유저의 이름(닉네임)을 갱신한다. timezone 과 마찬가지로 better-auth
 * 클라이언트가 갱신 후 세션을 다시 읽어오므로 별도 캐시 무효화는 필요 없다. */
export function useUpdateNicknameMutation() {
  return useMutation({
    mutationFn: async (name: string) => {
      const { error } = await authClient.updateUser({ name });
      if (error) {
        const descriptor = authServerErrorDescriptor(error.code);
        throw new Error(descriptor ? i18n._(descriptor) : t(i18n)`이름을 저장하지 못했습니다.`);
      }
    },
  });
}

/** 크롭된 아바타 blob(큰 원본 + 작은 변형본)을 R2 에 올리고(POST /api/users/me/avatar),
 * 받은 public url(큰 원본)을 authClient.updateUser 로 user.image 에 반영한다. 작은
 * 변형본 URL 은 저장하지 않고 표시 시점에 smallAvatarUrl 로 파생한다. 업로드 라우트가
 * DB 를 직접 쓰지 않는 이유는 authClient.updateUser 를 거쳐야 세션 캐시(useSession)가
 * 함께 갱신되기 때문이다 — 이름/타임존과 동일한 경로. */
export function useUploadAvatarMutation() {
  return useMutation({
    mutationFn: async ({ image, imageSm }: { image: Blob; imageSm: Blob }) => {
      // hono client 가 form 필드로 multipart 를 구성한다(boundary 자동). form 값은
      // File 이어야 하므로(ParsedFormValue) 크롭 결과 Blob 을 File 로 감싼다 — 서버는
      // instanceof Blob 로 받으므로 File(⊂Blob)도 통과한다.
      const res = await apiClient.api.users.me.avatar.$post({
        form: {
          image: new File([image], "avatar.webp", { type: image.type }),
          imageSm: new File([imageSm], "avatar-sm.webp", { type: imageSm.type }),
        },
      });
      // 응답이 실패했는지
      const isResNotOk = !res.ok;
      if (isResNotOk) throw new Error(t(i18n)`프로필 사진을 저장하지 못했습니다.`);
      const { url } = (await res.json()) as { url: string };
      const { error } = await authClient.updateUser({ image: url });
      if (error) {
        const descriptor = authServerErrorDescriptor(error.code);
        throw new Error(descriptor ? i18n._(descriptor) : t(i18n)`프로필 사진을 저장하지 못했습니다.`);
      }
    },
  });
}
